import { useEffect, useRef, useState } from 'react';
import { useSteleStore } from '@/state/store';
import { i18n } from '@/i18n/dict';
import { useWebRtc } from '@/hooks/useWebRtc';
import { useNfcBridge } from '@/hooks/useNfcBridge';

function useT() {
  const lang = useSteleStore((s) => s.lang);
  return i18n[lang];
}

function H(html: string) {
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57a1.02 1.02 0 0 0-1.02.24l-2.2 2.2a15.074 15.074 0 0 1-6.59-6.58l2.2-2.21c.27-.27.35-.66.24-1.02C8.7 6.45 8.5 5.25 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z" />
    </svg>
  );
}

function StartCallCta({ darkBg }: { darkBg?: boolean }) {
  const t = useT();
  const startCall = useSteleStore((s) => s.startCall);

  return (
    <div className={`cta-primary ${darkBg ? 'on-dark' : ''}`} onClick={() => startCall()}>
      <div className="cta-ring">
        <PhoneIcon />
      </div>
      <div className="cta-label">{t.startCall}</div>
    </div>
  );
}

export function IdleOrb() {
  const t = useT();
  const identity = useSteleStore((s) => s.identity);
  const agentsAvailable = useSteleStore((s) => s.agentsAvailable);
  const availabilityNotice = useSteleStore((s) => s.availabilityNotice);
  const noAgent = agentsAvailable === false; // null/true → Start zeigen (forward-compatible)

  return (
    <div className="state state-idle-orb active">
      <div className="idle-orb-visual">
        <div className="orb-secondary"></div>
        <div className="orb"></div>
      </div>
      <div className="tenant-mark">
        {identity?.logo
          ? <img src={identity.logo} alt="" className="tenant-logo" style={{ objectFit: 'contain', borderRadius: 0, background: 'transparent' }} />
          : <div className="tenant-logo">{(identity?.tenantName || t.tenantName || 'S').charAt(0).toUpperCase()}</div>}
        <div className="tenant-name">{identity?.tenantName || t.tenantName}</div>
        <div className="tenant-location">{identity?.location || t.tenantLoc}</div>
      </div>
      <div className="idle-center">
        <div className="idle-eyebrow">{t.welcome}</div>
        {noAgent
          ? <>
              <div className="idle-title">{H(`<strong>${t.noAgentTitle}</strong>`)}</div>
              <div className="idle-subtitle" style={{ marginTop: 10, opacity: 0.75, fontSize: '0.9em', maxWidth: '28ch', marginLeft: 'auto', marginRight: 'auto' }}>{availabilityNotice || t.noAgentDesc}</div>
            </>
          : <div className="idle-title">{H(t.idleTitle)}</div>}
      </div>
      {!noAgent && <StartCallCta />}
    </div>
  );
}

function useClock() {
  const [time, setTime] = useState(() => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  });
  useEffect(() => {
    const id = setInterval(() => {
      const d = new Date();
      setTime(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
    }, 30000);
    return () => clearInterval(id);
  }, []);
  return time;
}

export function IdleContent() {
  const t = useT();
  const identity = useSteleStore((s) => s.identity);
  const time = useClock();

  return (
    <div className="state state-idle-content active">
      <div className="idle-content-bg"></div>
      <div className="content-overlay">
        <div className="content-tenant">
          {identity?.logo
            ? <img src={identity.logo} alt="" className="content-tenant-mark" style={{ objectFit: 'contain', background: 'transparent' }} />
            : <div className="content-tenant-mark">{(identity?.tenantName || t.tenantName || 'S').charAt(0).toUpperCase()}</div>}
          <div className="content-tenant-name">{identity?.tenantName || t.tenantName}</div>
        </div>
        <div className="content-headline">{H(t.contentHeadline)}</div>
        <div className="content-sub">{t.contentSub}</div>
        <div className="content-meta">
          <div className="content-time">
            <div className="content-time-clock">{time}</div>
            <div className="content-time-date">{t.contentDate}</div>
          </div>
          <StartCallCta darkBg />
        </div>
      </div>
    </div>
  );
}

export function Calling() {
  const t = useT();
  const setView = useSteleStore((s) => s.setView);

  return (
    <div className="state state-calling active">
      <div className="calling-visual">
        <div className="calling-pulse"></div>
        <div className="calling-pulse"></div>
        <div className="calling-pulse"></div>
        <div className="calling-core">
          <PhoneIcon />
        </div>
      </div>
      <div className="calling-title">{t.callingTitle}</div>
      <div className="calling-sub">{t.callingSub}</div>
      <button className="cta-secondary" onClick={() => setView('idle-orb')}>
        {t.cancel}
      </button>
    </div>
  );
}

function CallTimer() {
  const [secs, setSecs] = useState(222);
  useEffect(() => {
    const id = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const m = Math.floor(secs / 60);
  const s = (secs % 60).toString().padStart(2, '0');
  return <span>{`${m}:${s}`}</span>;
}

function HangupIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" />
    </svg>
  );
}

function TranslationLayer() {
  const connectedAgentName = useSteleStore((s) => s.connectedAgentName);
  return (
    <div className="translation-layer">
      <div className="tx-bubble from-agent">
        <div className="tx-meta">{connectedAgentName || 'Ihr Berater'} · DE → TR</div>
        Merhaba, size nasıl yardımcı olabilirim?
      </div>
      <div className="tx-bubble from-self">
        <div className="tx-meta">Sen · TR → DE</div>
        Bir konto açmak istiyorum.
      </div>
    </div>
  );
}

function LangOverlay() {
  const open = useSteleStore((s) => s.langOverlayOpen);
  const close = useSteleStore((s) => s.closeLangOverlay);
  const t = useT();

  const tiles = [
    ['🇩🇪', 'Deutsch'],
    ['🇬🇧', 'English'],
    ['🇹🇷', 'Türkçe'],
    ['🇸🇦', 'العربية'],
    ['🇫🇷', 'Français'],
    ['🇪🇸', 'Español'],
    ['🇮🇹', 'Italiano'],
    ['🇵🇱', 'Polski'],
    ['🇺🇦', 'Українська'],
  ];

  return (
    <div className={`lang-overlay ${open ? 'active' : ''}`}>
      <div className="lang-card">
        <div className="lang-title">{t.langTitle}</div>
        <div className="lang-sub">{t.langSub}</div>
        <div className="lang-grid">
          {tiles.map(([flag, name]) => (
            <div key={name} className="lang-tile" onClick={close}>
              <div className="lang-flag">{flag}</div>
              <div className="lang-name">{name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ConsentSheet() {
  const open = useSteleStore((s) => s.consentOpen);
  const respond = useSteleStore((s) => s.respondConsent);
  const notice = useSteleStore((s) => s.consentNotice);
  const t = useT();

  return (
    <>
      <div className={`consent-backdrop ${open ? 'active' : ''}`}>
        <div className="consent-sheet">
          <div className="consent-icon">
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="8" />
            </svg>
          </div>
          <div className="consent-title">{t.consentTitle}</div>
          <div className="consent-text">{t.consentText}</div>
          <div className="consent-actions">
            <button className="btn-decline" onClick={() => respond(false)}>
              {t.decline}
            </button>
            <button className="btn-accept" onClick={() => respond(true)}>
              {t.accept}
            </button>
          </div>
        </div>
      </div>
      {notice === 'cancelled' && (
        <div style={{
          position: 'absolute', left: '7%', right: '7%', bottom: '8%', zIndex: 11,
          background: 'rgba(0,0,0,0.82)', color: '#fff', borderRadius: 12,
          padding: '14px 18px', fontSize: 14, textAlign: 'center',
          backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        }}>
          {t.consentCancelled}
        </div>
      )}
    </>
  );
}

export function InCall() {
  const t = useT();
  const connectedAgentName = useSteleStore((s) => s.connectedAgentName);
  const endCall = useSteleStore((s) => s.endCall);
  const volume = useSteleStore((s) => s.volume);
  const setVolume = useSteleStore((s) => s.setVolume);
  const translationOn = useSteleStore((s) => s.translationOn);
  const rtcToken = useSteleStore((s) => s.rtcToken);
  // Stele-ID aus URL extrahieren (für video-settings-Abruf)
  const steleId = new URLSearchParams(window.location.search).get('stele');
  const rtc = useWebRtc({
    rtcToken,
    role: 'stele',
    steleId,
    onPeerGone: (reason) => {
      console.log('[incall] peer gone:', reason);
      useSteleStore.getState().endCall();
    },
  });

  const remoteRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const localRef = useRef<HTMLVideoElement>(null);
  const expertRef = useRef<HTMLVideoElement>(null); // Welle 8d/M2: zugeschalteter Experte

  // Welle 6: NFC-Bridge zum lokalen Windows-Client.
  // Aktiv solange die Stele in einer Call-View ist (egal ob bereits connected).
  // Bei jedem nfcRead leiten wir den Payload via socket weiter an den Berater.
  useNfcBridge({
    enabled: true,
    onEvent: (payload) => {
      const ok = rtc.sendNfcEvent(payload as unknown as Record<string, unknown>);
      if (!ok) console.warn('[nfc] payload konnte nicht ans Backend gesendet werden (kein socket)');
    },
    onStatusChange: (status) => {
      // dezent loggen, kein UI-Block
      console.log('[nfc] bridge status:', status);
    },
  });

  // Welle 8c: object-fit am Berater-Hauptbild nach Seitenverhältnis. Hochformat
  // (Berater-Kamera, Portrait-Pipeline) → 'cover' wie bisher. Querformat (geteilter
  // Bildschirm) → 'contain', damit der gesamte Inhalt sichtbar ist (Balken oben/unten
  // statt seitlichem Beschnitt). Erkennung per 'resize'-Event des Video-Elements,
  // das auch beim replaceTrack-Wechsel (Kamera↔Bildschirm) feuert — keine Protokolländerung.
  const [mainFit, setMainFit] = useState<'cover' | 'contain'>('cover');
  // Welle 8c Phase B: Hauptbild-Quelle. Teilt der Companion seinen Bildschirm, hat das
  // Vorrang vor dem A/V-Berater-Bild; beim Beenden fällt es automatisch zurück.
  const mainStream = rtc.companionScreenStream ?? rtc.remoteStreamMain;
  const companionSharing = !!rtc.companionScreenStream;
  // Welle 8d/M2: Ist ein Experte zugeschaltet, teilen wir das Hauptbild (Berater oben,
  // Experte unten). hasExpert steuert das Layout UND triggert das Re-Binding der
  // Video-Elemente (das remoteRef-Element wandert beim Umschalten im DOM).
  const hasExpert = !!rtc.remoteExpertStream;
  // Welle 8d/M2: Anzeige nach schwächstem Teilnehmer. Bei zugeschaltetem Experten (3+)
  // pausiert SFrame → nur noch transportverschlüsselt. Der Indikator unterscheidet das.
  const e2eeActive = Boolean(rtc.connectionInfo?.e2eeActive);
  useEffect(() => {
    const v = remoteRef.current;
    if (!v) return;
    const update = () => {
      if (v.videoWidth > 0 && v.videoHeight > 0) {
        setMainFit(v.videoWidth > v.videoHeight ? 'contain' : 'cover');
      }
    };
    update();
    v.addEventListener('resize', update);
    v.addEventListener('loadedmetadata', update);
    return () => {
      v.removeEventListener('resize', update);
      v.removeEventListener('loadedmetadata', update);
    };
  }, [mainStream, hasExpert]);

  useEffect(() => {
    if (remoteRef.current && mainStream) remoteRef.current.srcObject = mainStream;
  }, [mainStream, hasExpert]);
  useEffect(() => {
    if (remoteAudioRef.current && rtc.remoteStreamAudio) remoteAudioRef.current.srcObject = rtc.remoteStreamAudio;
  }, [rtc.remoteStreamAudio]);
  useEffect(() => {
    if (localRef.current && rtc.localStreamMain) localRef.current.srcObject = rtc.localStreamMain;
  }, [rtc.localStreamMain]);
  // Welle 8d/M2: Experten-Stream (Audio+Video) an die Kachel binden. NICHT gemutet —
  // der Kunde soll den Experten hören.
  useEffect(() => {
    if (expertRef.current && rtc.remoteExpertStream) {
      expertRef.current.srcObject = rtc.remoteExpertStream;
      expertRef.current.play().catch(() => { /* Autoplay ggf. blockiert */ });
    }
  }, [rtc.remoteExpertStream]);

  return (
    <div className={`state state-incall active ${translationOn ? 'with-translation' : ''}`}>
      <div className="incall-video-main">
        {hasExpert ? (
          // Welle 8d/M2: Split-Ansicht — Berater oben, Experte unten (Stele ist Portrait).
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', flex: 1, minHeight: 0, background: '#000' }}>
              {mainStream ? (
                <video ref={remoteRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: mainFit, background: '#000' }} />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div className="incall-avatar">{rtc.status === 'waiting-offer' || rtc.status === 'connecting' ? '⌛' : 'AM'}</div>
                </div>
              )}
              <div style={{
                position: 'absolute', bottom: 8, left: 8, zIndex: 5,
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: '#000000aa', color: '#fff', padding: '4px 10px',
                borderRadius: 999, fontSize: 11, fontWeight: 600, letterSpacing: '.04em',
                backdropFilter: 'blur(4px)',
              }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--tenant-accent, #7c3aed)' }} />
                {companionSharing ? 'Bildschirmfreigabe' : 'Berater'}
              </div>
            </div>
            <div style={{ position: 'relative', flex: 1, minHeight: 0, background: '#000', borderTop: '3px solid var(--tenant-accent, #7c3aed)' }}>
              <video ref={expertRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{
                position: 'absolute', bottom: 8, left: 8, zIndex: 5,
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: '#000000aa', color: '#fff', padding: '4px 10px',
                borderRadius: 999, fontSize: 11, fontWeight: 600, letterSpacing: '.04em',
                backdropFilter: 'blur(4px)',
              }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--tenant-accent, #7c3aed)' }} />
                {(rtc.expertName || 'Experte')} · zugeschaltet
              </div>
            </div>
          </div>
        ) : (
          <>
            {mainStream ? (
              <video ref={remoteRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: mainFit, background: '#000', borderRadius: 'inherit' }} />
            ) : (
              <div className="incall-avatar">{rtc.status === 'waiting-offer' || rtc.status === 'connecting' ? '⌛' : 'AM'}</div>
            )}
            {companionSharing && (
              <div style={{
                position: 'absolute', top: 12, left: 12, zIndex: 5,
                display: 'inline-flex', alignItems: 'center', gap: 7,
                background: '#000000aa', color: '#fff', padding: '5px 11px',
                borderRadius: 999, fontSize: 12, fontWeight: 600, letterSpacing: '.04em',
                backdropFilter: 'blur(4px)',
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--tenant-accent, #7c3aed)', display: 'inline-block' }} />
                Bildschirmfreigabe
              </div>
            )}
          </>
        )}
        {/* Welle 4f: separates Audio-Element für dauerhaften Audio-Stream */}
        <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: 'none' }} />
      </div>
      <div className="incall-top-bar">
        <span className="live-dot"></span>
        <span>
          {(connectedAgentName || t.liveWith)} · <CallTimer />
          {rtc.status !== 'connected' && rtc.status !== 'idle' && (
            <span style={{ marginLeft: 8, opacity: 0.7, fontSize: '0.85em' }}>· {rtc.status}</span>
          )}
        </span>
        {/* Welle 7e: Aufzeichnungs-Indikator — Transparenz für den Gast */}
        {rtc.remoteRecording.active && (
          <span
            style={{
              marginLeft: 12, display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(220,38,38,0.92)', color: '#fff', padding: '4px 11px',
              borderRadius: 20, fontSize: '0.8em', fontWeight: 700,
            }}
            title="Dieses Gespräch wird aufgezeichnet"
          >
            <span style={{
              width: 9, height: 9, borderRadius: '50%', background: '#fff',
              animation: 'recBlink 1.2s ease-in-out infinite',
            }} />
            Aufzeichnung
          </span>
        )}
        {/* Welle 7a/8d: Verschlüsselungs-Hinweis — unterscheidet Ende-zu-Ende von
            nur transportverschlüsselt (sobald ein Experte zugeschaltet ist). */}
        {(rtc.connectionInfo?.connected || rtc.status === 'connected') && (
          <span
            style={{
              marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
              background: e2eeActive ? 'rgba(34,197,94,0.18)' : 'rgba(245,158,11,0.20)',
              color: e2eeActive ? '#bbf7d0' : '#fde68a',
              padding: '4px 10px', borderRadius: 20,
              fontSize: '0.8em', fontWeight: 600,
            }}
            title={e2eeActive
              ? 'Diese Verbindung ist Ende-zu-Ende verschlüsselt.'
              : 'Transportverschlüsselt (DTLS-SRTP). Während ein weiterer Teilnehmer zugeschaltet ist, ist die Ende-zu-Ende-Verschlüsselung pausiert.'}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            {e2eeActive ? 'Ende-zu-Ende' : 'Transportverschlüsselt'}
          </span>
        )}
      </div>
      <div className="incall-selfview">
        {rtc.localStreamMain ? (
          <video ref={localRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
        ) : (
          t.selfView
        )}
      </div>

      <TranslationLayer />

      <div className="incall-controls">
        <div className="volume-compact">
          <button className="vol-btn" onClick={() => setVolume(-1)}>
            −
          </button>
          <div className="vol-level">{volume}</div>
          <button className="vol-btn" onClick={() => setVolume(1)}>
            +
          </button>
        </div>
        <button className="btn-hangup-ios" onClick={() => { rtc.hangup(); endCall(); }} title="Auflegen">
          <HangupIcon />
        </button>
      </div>

      <LangOverlay />
      <ConsentSheet />
    </div>
  );
}

// (DocCamToggle entfernt — Doc-Cam-Switch wird in Welle 4a vom Berater
//  ausgelöst via rtc:request-switch; die Stele führt nur aus.)

export function Waiting() {
  const t = useT();
  const setView = useSteleStore((s) => s.setView);
  return (
    <div className="state state-waiting active">
      <div className="waiting-icon">
        <svg viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7 V 12 L 15 14" />
        </svg>
      </div>
      <div className="waiting-title">{t.waitingTitle}</div>
      <div className="waiting-time">4 min</div>
      <div className="waiting-time-label">{t.waitLabel}</div>
      <div className="waiting-pos">
        <div className="pos-dot"></div>
        <span>{t.waitPos}</span>
      </div>
      <button className="cta-secondary" onClick={() => setView('idle-orb')}>
        {t.bookApp}
      </button>
    </div>
  );
}

export function Unavailable() {
  const t = useT();
  const setView = useSteleStore((s) => s.setView);
  const unavailReason = useSteleStore((s) => s.unavailReason);
  const noAgentNotice = useSteleStore((s) => s.noAgentNotice);
  const noAgent = unavailReason === 'no-agent';
  const title = noAgent ? t.noAgentTitle : t.unavailTitle;
  const desc = noAgent ? (noAgentNotice || t.noAgentDesc) : t.unavailDesc;
  return (
    <div className="state state-unavailable active">
      <div className="unavail-icon">
        <svg viewBox="0 0 24 24">
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3 V 7" />
          <path d="M8 3 V 7" />
          <path d="M3 11 H 21" />
        </svg>
      </div>
      <div className="unavail-title">{title}</div>
      <div className="unavail-desc">{desc}</div>
      {!noAgent && <div className="unavail-hours">{t.serviceHours}</div>}
      <button className="cta-secondary" onClick={() => setView('idle-orb')}>
        {t.bookApp}
      </button>
    </div>
  );
}

export function Feedback() {
  const t = useT();
  const rating = useSteleStore((s) => s.rating);
  const setRating = useSteleStore((s) => s.setRating);
  const setView = useSteleStore((s) => s.setView);
  const steleId = useSteleStore((s) => s.steleId);

  const [timeoutSecs, setTimeoutSecs] = useState(15);
  const [remaining, setRemaining] = useState(15);
  const submittedRef = useRef(false);

  // Welle 5c: feedbackTimeoutSeconds aus Stele-Settings laden (Default 15)
  useEffect(() => {
    if (!steleId) return;
    let cancelled = false;
    fetch(`/api/steles/${steleId}/video-settings`)
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => {
        if (cancelled || !s) return;
        const t = typeof s.feedbackTimeoutSeconds === 'number' ? s.feedbackTimeoutSeconds : 15;
        setTimeoutSecs(t);
        setRemaining(t);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [steleId]);

  // Auto-Timeout: läuft solange noch nichts gewählt wurde
  useEffect(() => {
    if (timeoutSecs <= 0) return; // 0 = kein Auto-Skip
    if (submittedRef.current) return;
    if (remaining <= 0) {
      // Timeout abgelaufen: zurück zu idle ohne Bewertung
      setRating(0);
      setView('idle-orb');
      return;
    }
    const id = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(id);
  }, [remaining, timeoutSecs, setView, setRating]);

  function submit(r: number) {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setRating(r);
    // Welle 6: rating ans Backend posten (fire-and-forget, blockt UI nicht)
    const lastCallId = useSteleStore.getState().lastCallId;
    if (lastCallId) {
      fetch(`/api/calls/${lastCallId}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: r }),
      })
        .then((res) => {
          if (!res.ok) console.warn('[feedback] post failed', res.status);
        })
        .catch((e) => console.warn('[feedback] post error', e));
    } else {
      console.warn('[feedback] kein lastCallId, rating nicht persistiert');
    }
    setView('goodbye');
    setTimeout(() => {
      setRating(0);
      setView('idle-orb');
    }, 3000);
  }

  return (
    <div className="state state-feedback active">
      <div className="feedback-title">{t.feedbackTitle}</div>
      <div className="feedback-sub">{t.feedbackSub}</div>
      <div className="feedback-options" style={{ gap: 12 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            className="feedback-star"
            onClick={() => submit(n)}
            aria-label={`${n} Stern${n === 1 ? '' : 'e'}`}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 8,
              cursor: 'pointer',
              lineHeight: 0,
            }}
          >
            <svg
              width="56"
              height="56"
              viewBox="0 0 24 24"
              fill={(rating ?? 0) >= n ? '#FFB400' : 'none'}
              stroke={(rating ?? 0) >= n ? '#FFB400' : 'currentColor'}
              strokeWidth="1.5"
              strokeLinejoin="round"
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </button>
        ))}
      </div>
      {timeoutSecs > 0 && !submittedRef.current && (
        <div
          className="feedback-timer"
          style={{ marginTop: 16, opacity: 0.55, fontSize: '0.85em' }}
        >
          {remaining}s
        </div>
      )}
    </div>
  );
}

export function Goodbye() {
  const t = useT();
  return (
    <div className="state state-goodbye active">
      <div className="goodbye-check">
        <svg viewBox="0 0 24 24">
          <path d="M5 12 L 10 17 L 19 7" />
        </svg>
      </div>
      <div className="goodbye-title">{t.goodbyeTitle}</div>
      <div className="goodbye-sub">{t.goodbyeSub}</div>
    </div>
  );
}
